import { AlertTriangle } from 'lucide-react';
import { useAuth, type TenantResolutionError } from '../providers/AuthProvider';

const REASON_COPY: Record<TenantResolutionError['reason'], { headline: string; detail: string }> = {
  no_org_claim: {
    headline: 'Your account has no workspace link',
    detail:
      'You signed in successfully, but your account is not associated with any workspace in Apex. Share the identifiers below with your admin — they can seed your membership or fix your organization assignment.',
  },
  no_tenant_for_org: {
    headline: 'Your organization is not registered in Apex',
    detail:
      'You signed in successfully, but the organization on your account is not wired to an Apex workspace yet. Share the identifiers below with your admin so they can finish the setup.',
  },
  no_membership: {
    headline: 'Your account is not a member of this workspace',
    detail:
      'You signed in successfully, but you are not a member of the Apex workspace for this organization. Share the identifiers below with your admin so they can add you.',
  },
};

/**
 * Rendered by the app shell when /auth/tenant-status reports that the
 * current user authenticated but couldn't resolve to a tenant. Shows the
 * minimum diagnostic identifiers an admin needs to fix the underlying
 * data/config issue and deliberately avoids any "try again" that would
 * push the user back through /auth/login — that's what produced the
 * redirect loop this page exists to prevent.
 */
export function TenantErrorPage() {
  const { tenantError, logout } = useAuth();
  if (!tenantError) return null;

  const copy = REASON_COPY[tenantError.reason];
  const { sub, email, organizationId, timestamp } = tenantError.diagnostics;

  return (
    <div className="size-full flex items-center justify-center bg-background p-6">
      <div className="max-w-xl w-full bg-card border border-border/60 rounded-3xl shadow-sm p-10">
        <div className="flex items-start gap-4 mb-6">
          <AlertTriangle
            className="w-6 h-6 text-amber-500 shrink-0 mt-0.5"
            strokeWidth={1.75}
            aria-hidden="true"
          />
          <div>
            <h1 className="text-[1.0625rem] font-medium text-foreground mb-2">{copy.headline}</h1>
            <p className="text-[0.8125rem] leading-relaxed text-muted-foreground">{copy.detail}</p>
          </div>
        </div>

        <dl className="text-[0.75rem] font-mono bg-muted/40 rounded-2xl p-5 space-y-2">
          <DiagRow label="sub" value={sub} />
          <DiagRow label="email" value={email} />
          <DiagRow label="organization_id" value={organizationId ?? '(none)'} />
          <DiagRow label="timestamp" value={timestamp} />
        </dl>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-[0.8125rem] px-4 py-2 rounded-full border border-border/60 hover:bg-muted/60 transition"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => {
              void logout();
            }}
            className="text-[0.8125rem] px-4 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function DiagRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-muted-foreground min-w-[96px]">{label}</dt>
      <dd className="text-foreground break-all">{value}</dd>
    </div>
  );
}
